import React, { useState, useEffect } from 'react';
import { FaBook, FaTimes, FaPlus, FaTrash, FaEdit, FaSave, FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';

const TravelNotes = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [wishlist, setWishlist] = useState([]);
  const [newWishlistItem, setNewWishlistItem] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['sights', 'food', 'accommodation', 'shopping', 'other']);

  // Load saved data from localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem('travelNotes');
    const savedWishlist = localStorage.getItem('travelWishlist');
    
    if (savedNotes) setNotes(JSON.parse(savedNotes));
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  // Save data to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('travelNotes', JSON.stringify(notes));
  }, [notes]);

  useEffect(() => {
    localStorage.setItem('travelWishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // Note functions
  const addNote = () => {
    if (!newNote.trim()) return;
    
    const note = {
      id: uuidv4(),
      text: newNote,
      category: selectedCategory,
      createdAt: new Date().toISOString(),
      isPinned: false
    };
    
    setNotes([...notes, note]);
    setNewNote('');
  };

  const deleteNote = (id) => {
    setNotes(notes.filter(note => note.id !== id));
  };

  const startEditing = (note) => {
    setEditingNoteId(note.id);
    setEditText(note.text);
  };

  const saveEdit = (id) => {
    setNotes(notes.map(note => 
      note.id === id ? { ...note, text: editText } : note
    ));
    setEditingNoteId(null);
  };

  const togglePin = (id) => {
    setNotes(notes.map(note => 
      note.id === id ? { ...note, isPinned: !note.isPinned } : note
    ));
  };

  // Wishlist functions
  const addToWishlist = () => {
    if (!newWishlistItem.trim()) return;
    
    const wishlistItem = {
      id: uuidv4(),
      text: newWishlistItem,
      category: selectedCategory,
      isCompleted: false,
      priority: 'medium',
      notes: ''
    };
    
    setWishlist([...wishlist, wishlistItem]);
    setNewWishlistItem('');
  };

  const toggleWishlistItem = (id) => {
    setWishlist(wishlist.map(item => 
      item.id === id ? { ...item, isCompleted: !item.isCompleted } : item
    ));
  };

  const deleteWishlistItem = (id) => {
    setWishlist(wishlist.filter(item => item.id !== id));
  };

  // Filter functions
  const filteredNotes = selectedCategory === 'all' 
    ? notes 
    : notes.filter(note => note.category === selectedCategory);

  const filteredWishlist = selectedCategory === 'all'
    ? wishlist
    : wishlist.filter(item => item.category === selectedCategory);

  // Sort pinned notes to the top
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });

  return (
    <>
      {/* Floating Action Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        style={styles.floatingButton}
        title="Travel Notes"
      >
        <FaBook size={24} />
      </button>

      {/* Notes Panel */}
      {isOpen && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <h3 style={styles.panelTitle}>
              {activeTab === 'notes' ? 'Travel Notes' : 'Wishlist'}
            </h3>
            <button 
              onClick={() => setIsOpen(false)}
              style={styles.closeButton}
            >
              <FaTimes />
            </button>
          </div>

          {/* Tabs */}
          <div style={styles.tabs}>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'notes' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('notes')}
            >
              Notes
            </button>
            <button 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'wishlist' ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab('wishlist')}
            >
              Wishlist
            </button>
          </div>

          {/* Category Filter */}
          <div style={styles.categoryFilter}>
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <>
              <div style={styles.addNoteContainer}>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a new note..."
                    style={styles.input}
                    onKeyPress={(e) => e.key === 'Enter' && addNote()}
                    aria-label="New note"
                  />
                </div>
                <div style={{ position: 'relative' }}>
                  <select 
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    style={styles.categorySelect}
                    aria-label="Select category"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <button 
                  onClick={addNote}
                  style={styles.addButton}
                  title="Add Note"
                  aria-label="Add note"
                >
                  <FaPlus />
                  <span style={{ marginLeft: '6px', display: ['xs', 'sm'].includes(window.innerWidth) ? 'inline' : 'none' }}>Add</span>
                </button>
              </div>

              <div style={styles.notesList}>
                {sortedNotes.length === 0 ? (
                  <p style={styles.emptyState}>No notes yet. Add your first note!</p>
                ) : (
                  sortedNotes.map(note => (
                    <div 
                      key={note.id} 
                      style={{
                        ...styles.noteItem,
                        ...(note.isPinned ? styles.pinnedNote : {})
                      }}
                    >
                      <div style={styles.noteContent}>
                        {editingNoteId === note.id ? (
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            style={styles.editInput}
                            autoFocus
                          />
                        ) : (
                          <p style={styles.noteText}>{note.text}</p>
                        )}
                        <span style={styles.noteCategory}>{note.category}</span>
                      </div>
                      <div style={styles.noteActions}>
                        <button 
                          onClick={() => togglePin(note.id)}
                          style={styles.iconButton}
                          title={note.isPinned ? "Unpin" : "Pin to top"}
                        >
                          <FaMapMarkerAlt 
                            color={note.isPinned ? "#f59e0b" : "#9ca3af"} 
                          />
                        </button>
                        {editingNoteId === note.id ? (
                          <button 
                            onClick={() => saveEdit(note.id)}
                            style={styles.iconButton}
                            title="Save"
                          >
                            <FaSave color="#3b82f6" />
                          </button>
                        ) : (
                          <button 
                            onClick={() => startEditing(note)}
                            style={styles.iconButton}
                            title="Edit"
                          >
                            <FaEdit color="#3b82f6" />
                          </button>
                        )}
                        <button 
                          onClick={() => deleteNote(note.id)}
                          style={styles.iconButton}
                          title="Delete"
                        >
                          <FaTrash color="#ef4444" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Wishlist Tab */}
          {activeTab === 'wishlist' && (
            <>
              <div style={styles.addNoteContainer}>
                <input
                  type="text"
                  value={newWishlistItem}
                  onChange={(e) => setNewWishlistItem(e.target.value)}
                  placeholder="Add an item to your wishlist..."
                  style={styles.input}
                  onKeyPress={(e) => e.key === 'Enter' && addToWishlist()}
                />
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={styles.categorySelect}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
                <button 
                  onClick={addToWishlist}
                  style={styles.addButton}
                  title="Add to Wishlist"
                >
                  <FaPlus />
                </button>
              </div>

              <div style={styles.notesList}>
                {filteredWishlist.length === 0 ? (
                  <p style={styles.emptyState}>Your wishlist is empty. Add some places to visit!</p>
                ) : (
                  filteredWishlist.map(item => (
                    <div 
                      key={item.id} 
                      style={{
                        ...styles.wishlistItem,
                        ...(item.isCompleted ? styles.completedItem : {})
                      }}
                    >
                      <div style={styles.wishlistContent}>
                        <div 
                          style={styles.checkbox}
                          onClick={() => toggleWishlistItem(item.id)}
                        >
                          {item.isCompleted && <div style={styles.checkmark}>✓</div>}
                        </div>
                        <div>
                          <p style={styles.wishlistText}>
                            {item.text}
                            <span style={styles.wishlistCategory}>{item.category}</span>
                          </p>
                          {item.notes && (
                            <p style={styles.wishlistNotes}>{item.notes}</p>
                          )}
                        </div>
                      </div>
                      <div style={styles.noteActions}>
                        <button 
                          onClick={() => toggleWishlistItem(item.id)}
                          style={styles.iconButton}
                          title={item.isCompleted ? "Mark as pending" : "Mark as completed"}
                        >
                          <FaStar color={item.isCompleted ? "#f59e0b" : "#d1d5db"} />
                        </button>
                        <button 
                          onClick={() => deleteWishlistItem(item.id)}
                          style={styles.iconButton}
                          title="Remove"
                        >
                          <FaTrash color="#ef4444" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// Styles
const styles = {
  floatingButton: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#2563eb',
      transform: 'scale(1.05)'
    }
  },
  panel: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    width: '350px',
    maxHeight: '70vh',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    transform: 'translateY(0)'
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  panelTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: '#f3f4f6'
    }
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
    padding: '0 16px'
  },
  tabButton: {
    flex: 1,
    padding: '12px',
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderBottomWidth: '2px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    transition: 'all 0.2s',
    ':hover': {
      color: '#3b82f6',
      borderBottomColor: 'transparent'
    }
  },
  activeTab: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid'
  },
  categoryFilter: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    fontSize: '14px',
    color: '#374151',
    outline: 'none',
    ':focus': {
      borderColor: '#3b82f6',
      ring: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    }
  },
  addNoteContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: '12px 16px',
    gap: '8px',
    borderBottom: '1px solid #e5e7eb',
    '@media (max-width: 480px)': {
      flexDirection: 'column',
      gap: '10px',
      padding: '12px 12px'
    }
  },
  input: {
    flex: 1,
    minWidth: '120px',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    ':focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    },
    '@media (max-width: 480px)': {
      width: '100%',
      padding: '12px'
    }
  },
  categorySelect: {
    minWidth: '100px',
    padding: '10px 8px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    fontSize: '12px',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    ':focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    },
    '@media (max-width: 480px)': {
      width: '100%',
      padding: '12px 8px'
    }
  },
  addButton: {
    padding: '0 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    minWidth: '44px',
    height: '38px',
    ':hover': {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)'
    },
    ':active': {
      transform: 'translateY(0)'
    },
    '@media (max-width: 480px)': {
      width: '100%',
      height: '44px',
      marginTop: '4px'
    }
  },
  notesList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0'
  },
  emptyState: {
    padding: '24px 16px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px'
  },
  noteItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#f9fafb'
    }
  },
  pinnedNote: {
    backgroundColor: '#f0f9ff',
    borderLeft: '3px solid #0ea5e9'
  },
  noteContent: {
    marginBottom: '8px',
    position: 'relative'
  },
  noteText: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#111827',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  noteCategory: {
    display: 'inline-block',
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '10px',
    marginTop: '4px'
  },
  noteActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: '#f3f4f6',
      color: '#4b5563'
    }
  },
  editInput: {
    width: '100%',
    padding: '6px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    marginBottom: '8px',
    outline: 'none',
    ':focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    }
  },
  wishlistItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#f9fafb'
    }
  },
  completedItem: {
    opacity: 0.7,
    textDecoration: 'line-through',
    textDecorationColor: '#9ca3af',
    textDecorationThickness: '1px'
  },
  wishlistContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '8px'
  },
  checkbox: {
    minWidth: '20px',
    height: '20px',
    border: '2px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '2px',
    transition: 'all 0.2s',
    ':hover': {
      borderColor: '#9ca3af'
    }
  },
  checkmark: {
    color: '#10b981',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  wishlistText: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    color: '#111827',
    lineHeight: '1.5',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  wishlistCategory: {
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '10px',
    marginLeft: '4px'
  },
  wishlistNotes: {
    margin: '4px 0 0 0',
    fontSize: '12px',
    color: '#6b7280',
    fontStyle: 'italic',
    paddingLeft: '8px',
    borderLeft: '2px solid #e5e7eb'
  }
};

export default TravelNotes;
